import React, { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

export const SSNInput = (props) => {
  const {
    item,
    showPassword = false,
    value: propsValue = "",
    onChange,
    onBlur,
    currentPiiInfoFields,
    handleFieldLevelHandler,
    disabled = false,
    placeholder = "XXX-XX-XXXX",
    className = "",
    error = { isError: false, message: "" },
    ...restProps
  } = props;

  const [rawSSN, setRawSSN] = useState("");
  const [maskedSsn, setMaskedSsn] = useState("");

  useEffect(() => {
    const fieldValue = propsValue !== null ? propsValue : "";
    setRawSSN(format(fieldValue));
  }, [propsValue]);

  const replaceCharacters = (str) => {
    if (str?.length <= 1) {
      return str;
    }

    const lastCharacter = str.slice(-1);
    const replacedCharacters = str.slice(0, -1).replace(/[^-]/g, "X");
    return replacedCharacters + lastCharacter;
  };

  const maskAndSetData = () => {
    if (!showPassword) setMaskedSsn(rawSSN.replace(/\d/g, "X"));
    else {
      setMaskedSsn(rawSSN);
    }
  };

  useEffect(() => {
    let timer;
    // Replace characters with X after a second of typing
    if (rawSSN?.length > 0) {
      if (rawSSN.length > maskedSsn.length) {
        const replacedStr = replaceCharacters(rawSSN);
        setMaskedSsn(replacedStr);
        timer = setTimeout(() => {
          setMaskedSsn(rawSSN.replace(/\d/g, "X"));
        }, 500);
      } else {
        maskAndSetData();
      }
    } else {
      setMaskedSsn("");
    }

    return () => {
      clearTimeout(timer);
    };
  }, [rawSSN]);

  const stripDashes = (str) => str.replace(/-/g, "");

  const handleEmptyValue = (e) => {
    setRawSSN("");
    setMaskedSsn("");
    onChange?.(e, "");
  };

  const handleSingleCharacter = (value) => {
    setRawSSN(format(value.replace(/\D/g, "")));
  };

  const handleDeletion = (e) => {
    const rawDigits = stripDashes(rawSSN);
    const truncatedValue = rawDigits.slice(0, rawDigits.length - 1);
    setRawSSN(format(truncatedValue));
    onChange?.(e, truncatedValue);
  };

  const handleMaskedInput = (e, value) => {
    const numValue = value.replace(/\D/g, "");
    const rawDigits = stripDashes(rawSSN);
    const maxLength = 11;
    
    const newSSN = rawDigits.length > maxLength
      ? rawDigits.slice(0, maxLength) + numValue
      : rawDigits + numValue.charAt(numValue.length - 1);

    if (newSSN.length > 9) return;
    
    setRawSSN(format(newSSN));
    onChange?.(e, newSSN);
  };

  const handleVisibleInput = (e, value) => {
    setRawSSN(format(value));
    onChange?.(e, value);
  };

  const isDeleting = (value) => {
    return stripDashes(value).length < stripDashes(rawSSN).length;
  };

  const handleChange = (e) => {
    const { value } = e.target;

    if (value === "") {
      handleEmptyValue(e);
      return;
    }

    if (value.length === 1) {
      handleSingleCharacter(value);
      return;
    }

    if (isDeleting(value)) {
      handleDeletion(e);
      return;
    }

    if (!showPassword) {
      handleMaskedInput(e, value);
    } else {
      handleVisibleInput(e, value);
    }
  };

  const format = (v) => {
    if (!v) return v;
    v = v.slice(0, 11).replace(/-/g, "");
    if (v.length <= 3) {
      return v;
    }
    if (v.length > 3 && v.length <= 5) {
      return `${v.slice(0, 3)}-${v.slice(3)}`;
    }
    if (v.length > 5) {
      return `${v.slice(0, 3)}-${v.slice(3, 5)}-${v.slice(5)}`;
    }
  };

  // Helper function to mask SSN (similar to MUI reference maskSSN function)
  const maskSSN = (ssn) => {
    if (!ssn) {
      return "";
    }
    const maskedSSN = ssn.slice(-4);
    return `XXX-XX-${maskedSSN}`;
  };

  const inputValue = !showPassword ? rawSSN : maskSSN(propsValue);
  const isDisabled = disabled || showPassword;

  return (
    <div className="relative">
      <input
        {...restProps}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onBlur={(e) => {
          if (onBlur) onBlur(e, rawSSN.replace(/-/g, ""));
        }}
        autoComplete="off"
        disabled={isDisabled}
        placeholder={placeholder}
        className={`
          w-full px-3 py-2 pr-10 border rounded-md text-sm transition-colors
          ${
            error.isError
              ? "border-red-500 hover:border-red-500 focus:border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          }
          focus:outline-none focus:ring-1 font-mono tracking-wider
          ${className}
        `}
      />

      <button
        type="button"
        onClick={() => {
          return handleFieldLevelHandler(
            item,
            currentPiiInfoFields?.[item.uniqueField]
          );
        }}
        className={`
          absolute right-2 top-1/2 transform -translate-y-1/2
          p-1 rounded-md text-gray-500 hover:text-gray-600
          transition-colors duration-200
        `}
      >
        {!showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
      </button>
    </div>
  );
};
